import React from 'react';

export const joeBiden = {
  presidentNumber: 46,
  president: 'Joe Biden',
  subheader: 'The 46th President',
  years: '2021-2025',
  predecessor: 'Donald Trump',
  successor: null, 
  disclaimer: {
    text: 'This document is the verified original.',
    link: 'https://ballotpedia.org/Letters_left_by_U.S._presidents_to_their_successors'
  },
  letter: {
    header: 'Letter to Donald Trump (2025)',
    content: `Dear President Trump,

As I take leave of this sacred office I wish you and your family all the best in the next four years. The American people - and people around the world - look to this house for steadiness in the inevitable storms of history, and my prayer is that in the coming years will be a time of prosperity, peace, and grace for our nation.

May God bless you and guide you as He has blessed and guided our beloved country since our founding.

Joe Biden 1-20-25`
  },
  biographical: [
    'Born November 20, 1942, in Scranton, Pennsylvania',
    'Serving since January 20, 2021',
    'Oldest person elected president at age 78',
    'Previously served as Vice President (2009-2017) and U.S. Senator',
    'Longest-serving senator from Delaware'
  ],
  presidencyFacts: [
    'Led America through COVID-19 pandemic recovery',
    'Passed major infrastructure and climate legislation',
    'Appointed first Black woman to Supreme Court (Ketanji Brown Jackson)',
    'Led NATO response to Russian invasion of Ukraine',
    'Oversaw withdrawal from Afghanistan'
  ],
  milestones: [
    'COVID-19 vaccine rollout accelerated (2021)',
    'Infrastructure Investment and Jobs Act (2021)',
    'Inflation Reduction Act passed (2022)',
    'Russian invasion of Ukraine (2022)',
    'Reelection campaign for 2024'
  ],
  personalLife: [
    'Married Neilia Hunter in 1966, then Jill Jacobs in 1977',
    'Had four children, two from first marriage',
    'Suffered personal tragedies including wife and daughter\'s death',
    'Known for his empathy and bipartisan approach',
    'Currently serving as president'
  ],
  impactAndLegacy: [
    'Restored American leadership in international affairs',
    'Advanced climate change and infrastructure policies',
    'Appointed diverse federal judiciary',
    'Led economic recovery from pandemic',
    'Legacy still being written'
  ],
  postPresidency: [
    'Currently serving as president',
    'Running for reelection in 2024',
    'Legacy and impact still developing',
    'Future post-presidency plans unknown',
    'Will be oldest president if reelected'
  ],
  historicalAnalysis: `Joe Biden's presidency focused on restoring traditional governance norms and international alliances while addressing the COVID-19 pandemic, economic inequality, and threats to democratic institutions, achieving significant legislative victories amid continued political polarization.

[SECTION] COVID-19 Response and Pandemic Management

Biden inherited a pandemic that had killed over 400,000 Americans and devastated the economy, requiring immediate federal coordination and scientific expertise. The National Strategy for the COVID-19 Response emphasized vaccine distribution, school reopening, and economic recovery through federal coordination that had been lacking under his predecessor.

The successful vaccination campaign, distributing over 200 million shots in Biden's first 100 days, demonstrated effective federal crisis management while political polarization limited universal uptake. The American Rescue Plan Act (2021) provided $1.9 trillion in pandemic relief, including direct payments to individuals, extended unemployment benefits, and state and local government aid.

[SECTION] Infrastructure and Economic Legislation

The Infrastructure Investment and Jobs Act (2021) provided $1.2 trillion for traditional infrastructure while representing the largest federal investment in physical infrastructure since the Interstate Highway System. The CHIPS and Science Act (2022) invested $280 billion in semiconductor manufacturing and research, addressing supply chain vulnerabilities while competing with Chinese technological advancement.

The Inflation Reduction Act (2022) combined climate investment, healthcare cost reduction, and deficit reduction while representing the largest climate investment in American history. These achievements demonstrated that bipartisan legislation remained possible while positioning America for long-term economic competition with China and other rivals.

[SECTION] Climate Change and Energy Policy

Biden's climate agenda included rejoining the Paris Climate Agreement, setting aggressive emissions reduction targets, and investing hundreds of billions in clean energy technology. The Inflation Reduction Act's climate provisions used tax incentives and market mechanisms to accelerate renewable energy adoption and electric vehicle manufacturing.

However, the administration also approved some fossil fuel projects while balancing environmental goals with energy security and economic concerns.

[SECTION] Foreign Policy and Alliance Restoration

Biden emphasized rebuilding traditional alliances and multilateral institutions that had been strained during the Trump presidency. The chaotic withdrawal from Afghanistan in August 2021, while ending America's longest war, damaged Biden's credibility and resulted in terrorist attacks and humanitarian crisis.

Russia's invasion of Ukraine (2022) tested Biden's leadership and NATO unity, resulting in unprecedented Western sanctions and military aid that helped Ukraine resist Russian aggression. The strategic competition with China intensified through trade restrictions, technology controls, and military buildup while maintaining diplomatic engagement to prevent conflict.

[SECTION] Democracy and Institutional Protection

Biden's presidency emphasized protecting democratic institutions and norms that had been challenged during the Trump era and January 6 insurrection. The Justice Department's prosecution of January 6 participants and investigation of Trump's role demonstrated commitment to rule of law while raising questions about political prosecutions.

Voting rights legislation failed in Congress despite Democratic control, highlighting the limitations of presidential power when facing Senate filibuster rules.

[SECTION] Economic Recovery and Inflation

The economy experienced strong job growth and reduced unemployment during Biden's presidency while inflation reached levels not seen since the 1980s. The Federal Reserve's aggressive interest rate increases successfully reduced inflation but raised concerns about recession and economic stability.

Biden's economic policies, including substantial government spending and supply chain investments, contributed to both recovery and inflationary pressures.

[SECTION] Social and Cultural Issues

Biden's diverse cabinet appointments and judicial nominees increased representation while his Supreme Court appointment of Ketanji Brown Jackson marked historic progress. The administration's response to racial justice concerns included policing reform efforts and equity initiatives across federal agencies.

LGBTQ+ rights received strong support through executive actions and administrative policies, though legislative progress remained limited.

[SECTION] Immigration and Border Security

Biden reversed many Trump immigration policies while facing continued challenges at the southern border and congressional deadlock on comprehensive reform. The end of "Remain in Mexico" and family separation policies reflected humanitarian concerns while border encounters reached record levels during parts of his presidency.

The response to migration from Haiti, Venezuela, and other countries tested both humanitarian commitments and border security capabilities.

[SECTION] Congressional Relations and Bipartisanship

Despite narrow Democratic majorities, Biden achieved significant bipartisan legislation on infrastructure while facing complete Republican opposition on other priorities. The loss of House Democratic control in 2022 limited legislative possibilities while demonstrating continued voter concerns about economic and social issues.

Biden's Senate experience and relationships with Republicans enabled some bipartisan cooperation that had become rare in recent decades.

[SECTION] Technology and Information Warfare

The administration addressed social media misinformation and foreign interference while balancing free speech concerns with platform accountability. Cybersecurity threats from Russia, China, and other adversaries required new federal capabilities and public-private cooperation.

[SECTION] Judicial Appointments and Legal Legacy

Biden's federal judicial appointments emphasized diversity and professional qualifications while moving courts in a more liberal direction after Trump's conservative appointments. The Supreme Court's conservative majority, however, limited the administration's ability to advance liberal priorities on abortion, climate, and other issues.

[SECTION] 2024 Election and Democratic Transition

Biden's decision regarding the 2024 election reflected concerns about age, electability, and the future of democratic institutions. The peaceful transfer of power, regardless of electoral outcomes, remained a central concern given previous challenges to democratic norms.

[SECTION] Challenges and Limitations

Political polarization continued to limit governmental effectiveness while social media and partisan news sources reinforced political divisions. The persistence of cultural conflicts over race, gender, and social issues demonstrated the limitations of presidential leadership in healing social divisions.

Economic inequality remained significant despite job growth and social spending, highlighting structural challenges that require sustained policy attention.

[SECTION] Historical Assessment

Early assessment of Biden's presidency has emphasized his restoration of traditional governance norms and institutional competence while acknowledging the persistence of underlying political and social divisions. His legislative achievements, particularly on infrastructure and climate, represent significant accomplishments while foreign policy presents a mixed record of alliance restoration and strategic challenges.

The full historical impact of Biden's presidency will depend on the durability of his policy achievements and the long-term health of American democratic institutions. Biden's presidency demonstrates both the potential for experienced political leadership to achieve significant policy goals and the limitations of presidential power in addressing structural social and political problems.`,
  sources: [
    '[LINK:https://millercenter.org/president/biden|Miller Center, University of Virginia. "Joe Biden." Accessed 2025.]',
    '[LINK:https://www.history.com/topics/us-presidents/joe-biden|History.com. "Joe Biden: Facts, Presidency & Quotes." Updated February 2025.]',
    '[LINK:https://www.whitehouse.gov/administration/president-biden/|White House. "President Joe Biden." Accessed 2025.]',
    '[LINK:https://en.wikipedia.org/wiki/Presidency_of_Joe_Biden|Wikipedia. "Presidency of Joe Biden." Updated 2025.]',
    '[LINK:https://www.simonandschuster.com/books/The-Biden-Way/Bob-Woodward/9781982194566|Woodward, Bob. The Biden Way. Simon & Schuster, 2024.]',
    '[LINK:https://www.cbo.gov/publication/59847|Congressional Budget Office. "An Analysis of the President\'s 2025 Budget." 2024.]',
    '[LINK:https://www.pewresearch.org/politics/2024/01/24/biden-presidency-public-opinion-and-policy-assessment/|Pew Research Center. "Biden Presidency: Public Opinion and Policy Assessment." 2024.]'
  ]
};

export default joeBiden;